import React from 'react';
import {useDispatch} from 'react-redux';
import {
  Image,
  ScrollView,
  TouchableOpacity,
  View,
  Platform,
} from 'react-native';

import useWindowDimensionsSafe from 'src/Modules/Native/useWindowDimensionsSafe';

import VersionNumber from 'react-native-version-number';
import Constants from 'expo-constants';

import * as Updates from 'expo-updates';
import * as Application from 'expo-application';

import {UserProfileChallenges} from 'src/Data/UserProfileChallenges';

import {
  logOutUser,
  setCurrentGroupId,
} from 'src/Redux/reducers/user_info.reducer';

/////////////////////////////////////////////
///////  components
import UserProfileIcon from 'src/Modules/UserProfileIcon';
import {BodyText, ContinueButton} from 'src/Modules/CommonView';

import {
  tryGoogleLogout,
  tryFacebookLogout,
  tryFirebaseLogout,
} from 'src/Utils/loginFunctions';

import CheckBox from 'src/Modules/Native/CheckBox';
import {navigationRef} from 'src/Nav/navigationHelpers';
import {updateDataAtPath} from 'src/Utils/fireUtils';
import {useUserId, useUserInfo} from 'src/Hooks/reduxHooks';
import {mmkvStorage} from '../Utils/mmkvStorage';

const Profile = () => {
  const {width} = useWindowDimensionsSafe();

  const dispatch = useDispatch();

  const logout = () => {
    try {
      tryFirebaseLogout();
      dispatch(logOutUser());
      tryGoogleLogout()
        .then(() => {
          console.log('GOOGLE LOGGED OUT');
          return mmkvStorage.delete('userId');
        })
        .then(() => {
          console.log('FACEBOOK LOGGED OUT');
          return tryFacebookLogout();
        })
        .then(() => {
          dispatch(setCurrentGroupId(null));
        })
        .catch((error) => {
          console.log('Logout error: ', error);
        });
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <ScrollView>
      <UserInventorySection subversion={'v0'} />

      <ContinueButton
        text={'Sign Out'}
        background={global?.color?.gray3}
        handlePress={() => {
          navigationRef.goBack(null);
          setTimeout(() => {
            logout();
          }, 400);
        }}
        containerStyle={{
          width: width - 60,
          marginTop: 10,
          marginHorizontal: 30,
        }}
      />
    </ScrollView>
  );
};

const UserInventorySection = ({subversion}) => {
  const userId = useUserId();

  const notDiscoverable = useUserInfo('notDiscoverable');
  const email = useUserInfo('email');
  const badgesEarnedCount = useUserInfo('badgesEarnedCount');

  const toggleDiscoverable = async () => {
    console.log('toggleDiscoverable', {notDiscoverable});
    updateDataAtPath(
      `users/${userId || 'error'}/notDiscoverable`,
      !notDiscoverable,
    );
  };

  const appId = Application.applicationId;

  const currentHuntInfo = useUserInfo('currentHuntInfo');

  return (
    <SectionContainer>
      <TouchableOpacity
        style={{
          alignItems: 'center',
          paddingVertical: 10,
          borderBottomColor: global?.color?.white1,
          borderBottomWidth: 2,
          marginHorizontal: 30,
        }}
        onPress={() => null}
        activeOpacity={0.8}>
        <UserProfileIcon
          showName
          handlePress={() => {
            const challenge =
              UserProfileChallenges[
                Math?.floor(Math?.random() * UserProfileChallenges?.length)
              ];
            navigationRef.navigate('PhotoVideoCamera', {
              text: challenge,
              onCapture: (data) => {
                const {downloadURL, shareUUID} = data;
                updateDataAtPath(`/users/${userId || 1342342134}`, {
                  customPhotoUrl: downloadURL,
                  customPhotoShareUUID: shareUUID,
                  photoUrl: downloadURL,
                });
              },
              isVideo: false,
            });
          }}
          textColor={global?.color?.gray3}
          showCameraIcon={true}
        />
      </TouchableOpacity>
      <ProfileLine title={'Email:'} body={email} />
      <ProfileLine title={'User Id:'} body={userId} />
      {currentHuntInfo?.groupId && (
        <ProfileLine title={'Group Id:'} body={currentHuntInfo?.groupId} />
      )}
      <TouchableOpacity
        onPress={() => navigationRef.navigate('BadgesInfoModal')}
        underlayColor="#ffffff00">
        <ProfileLine title={'Total Badges:'} body={badgesEarnedCount + '+'} />
      </TouchableOpacity>

      <ProfileLine
        title={'Version Info:'}
        body={`${appId}-${
          VersionNumber?.appVersion || VersionNumber?.versionCode
        }-${subversion} (${Platform.OS})`}
      />

      <ProfileLine
        title={'Update Info:'}
        body={`${Constants?.manifest?.runtimeVersion}-${Updates?.releaseChannel}-${Updates?.updateId}`}
      />
      <ProfileLine isLast={true} />

      <TouchableOpacity
        style={{
          alignItems: 'center',
          flexDirection: 'row',
          marginHorizontal: 30,
          width: '100%',
          marginTop: -20,
        }}
        onPress={() => {
          toggleDiscoverable();
        }}>
        <CheckBox
          checkBoxColor={global?.color?.gray2}
          value={!notDiscoverable}
          onValueChange={() => toggleDiscoverable()}
          style={{width: 15, height: 15}}
        />
        <BodyText
          text={`Let other nearby potential players discover my group (You still have to approve them)`}
          textStyle={{
            marginBottom: 20,
            marginTop: 15,
            fontWeight: '700',
            flex: 1,
          }}
          left
        />
      </TouchableOpacity>
    </SectionContainer>
  );
};

const ProfileLine = ({title, body, image, isLast = false}) => {
  const {width} = useWindowDimensionsSafe();
  return (
    <View
      style={{
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginHorizontal: 10,
        minHeight: 40,
        width: width - 40,
        borderBottomColor: !isLast ? global?.color?.white1 : null,
        borderBottomWidth: !isLast ? 2 : 0,
      }}>
      <BodyText text={title} textStyle={{marginTop: 5}} />
      <View
        style={{
          alignItems: 'center',
          flexDirection: 'row',
        }}>
        <BodyText
          text={body}
          textStyle={{
            marginTop: 5,
            textAlign: 'right',
            marginRight: 2,
            width: 200,
            flexWrap: 'wrap',
            flexShrink: 1,
          }}
        />
        {!!image && (
          <Image
            style={{
              height: 28,
              width: 28,
              marginHorizontal: 2,
              resizeMode: 'contain',
            }}
            source={image}
          />
        )}
      </View>
    </View>
  );
};

const SectionContainer = ({children, handlePress}) => {
  const {width} = useWindowDimensionsSafe();
  return (
    <TouchableOpacity
      style={{
        backgroundColor: global?.color?.white,
        borderRadius: 10,
        marginHorizontal: 10,
        marginVertical: 10,
        paddingVertical: 5,
        position: 'relative',
        width: width - 20,
      }}
      onPress={handlePress}
      disabled={!handlePress}
      activeOpacity={0.8}>
      {children}
    </TouchableOpacity>
  );
};

export default Profile;
